import { inject } from '@angular/core';
import { UrlService } from '@uirouter/core';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { API_CONSTANTS } from '@client/app/core/constants/api.constant';
import { activeFeatures } from '@monsido/core/constants/general.constant';
import { LabelRepo } from '@monsido/modules/endpoints/api/label.repo';
import { PageService } from '@client/app/services/entities/page/page.service';
import { SpellingRepoService } from '@client/app/services/api/spelling-repo/spelling-repo.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { SessionService } from '@monsido/core/session/session.service';

export type BaseCurrentSort = {
    direction: 'asc' | 'desc';
    by: string;
};

type BaseControllerConfig = {
    table?: boolean,
    labels?: boolean,
    filters?: boolean,
    pages?: boolean,
};

export class BaseContainerComponentController {
    urlService = inject(UrlService);
    monEventsService = inject(MonEventService);

    labelRepo = inject(LabelRepo);
    pageService = inject(PageService);
    spellingRepoService = inject(SpellingRepoService);
    pipeService = inject(PipeService);
    sessionService = inject(SessionService);

    // Table
    page = 1;
    pageSize = API_CONSTANTS.PAGE_SIZE;
    activeTab = 'all';
    search: string | null = null;
    currentSort: BaseCurrentSort = { direction: 'desc', by: '' };

    // Labels and Filters
    labels: any[] = [];
    showLabels = activeFeatures.labels;
    selectedLabels: any[] = [];
    labelsPromise: Promise<void> | null = null;

    // Filters
    showFilters = activeFeatures.table_filters;
    availableFilters: any = {};
    isFiltersOpen = false;
    filters: any = {};

    // Pages
    selectedLanguages: any[] = [];
    isAdmin = false;

    constructor (private config: BaseControllerConfig) {
        const { table, labels, pages } = config;
        if (table) {
            this.tableInit();
        }
        if (labels) {
            this.labelsInit();
        }
        if (pages) {
            this.pagesInit();
        }
    }

    tableInit () {
        this.page = parseInt(this.urlService.search().page) || 1;
        this.pageSize = parseInt(this.urlService.search().page_size) || API_CONSTANTS.PAGE_SIZE;
    }

    labelsInit () {
        this.filters.labels = [];
        this.availableFilters.labels = [];
        this.getLabels();
    }

    pagesInit () {
        this.isAdmin = this.sessionService.hasRole('customer_admin');
        this.filters.languages = [];
        this.availableFilters.languages = [];
        this.getLanguages();
    }

    updateFilters (changes) {
        if (changes.changes) {
            changes = changes.changes;
        }
        this.updateLabelsFilters(changes);
        this.updatePagesFilters(changes);
    }

    /**
     * Abstract method that must be implemented by extending classes.
     */
    getPage (): void {
        throw new Error('getPage method must be implemented by extending class');
    }

    // Pages methods
    getLanguages () {
        this.spellingRepoService.getAllLanguages({}).then((data) => {
            this.availableFilters.languages = data.map((language) => {
                return { name: this.pipeService.get('translateLanguage')(language.lang), lang: language.lang };
            });
        }, () => {});
    }

    updateLabels (page) {
        const params: any = {};
        params.labels = page.labels.map((label) => {
            return label.id;
        });
        this.pageService.update(page, params).then(() => {}, () => {});
    }

    updatePagesFilters (changes) {
        if (changes.languages) {
            this.selectedLanguages = changes.languages.map((language) => {
                return language.lang;
            });
        } else {
            this.selectedLanguages = [];
        }
    }

    updateParamsWithFilters (params) {
        if (this.selectedLabels && this.selectedLabels.length > 0) {
            params['labels[]'] = this.selectedLabels;
        }

        if (this.selectedLanguages && this.selectedLanguages.length > 0) {
            params['languages[]'] = this.selectedLanguages;
        }

        return params;
    }

    // Labels methods
    getLabels () {
        const params = {
            page_size: 1000,
        };

        this.labelsPromise = this.labelRepo.getLabels(params).then((data) => {
            this.labels = data || [];
            this.availableFilters.labels = data || [];
        }, () => {});

        return this.labelsPromise;
    }

    updatePageLabels (label) {
        const params: any = {};
        params.labels = label.labels.map((l: any) => {
            return l.id;
        });

        this.pageService.update(label, params).then(() => {}, () => {});
    }

    updateLabelsFilters (changes) {
        if (changes.labels) {
            this.selectedLabels = changes.labels.map((label) => {
                return label.id;
            });
        } else {
            this.selectedLabels = [];
        }
    }

    // Table methods
    onStatus (tab: string) {
        this.page = 1;
        this.activeTab = tab;
        this.getPage();
    }

    onTabChange (tab: string) {
        this.activeTab = tab;
        this.page = 1;
        this.monEventsService.run('actionTrigger', { action: 'tab-change', params: { tab: tab } });
        this.getPage();
    }

    onSearch (search: string | null) {
        this.search = search;
        this.page = 1;
        this.monEventsService.run('actionTrigger', { action: 'search', params: { search: search } });
        this.getPage();
    }

    onSortPage (direction: BaseCurrentSort['direction'], by: string) {
        if (this.currentSort.by === by && this.currentSort.direction === direction) {
            return;
        }

        this.page = 1;
        this.currentSort = { by: by, direction: direction };
        this.monEventsService.run('actionTrigger', { action: 'table-sort', params: { by: by, direction: direction } });
        this.getPage();
    }

    onMonTableSort (currentSort: any) {
        if (!currentSort) {
            return;
        }

        if (currentSort.by === null && currentSort.direction === null) {
            // Reset sorting
            this.currentSort = currentSort;
            this.page = 1;
            this.monEventsService.run('actionTrigger', { action: 'table-sort', params: currentSort });
            this.getPage();
            return;
        }

        if (
            currentSort.by === this.currentSort.by &&
            currentSort.direction === this.currentSort.direction
        ) {
            return;
        }

        this.page = 1;
        this.currentSort = {
            by: currentSort.by,
            direction: currentSort.direction,
        };
        this.monEventsService.run('actionTrigger', { action: 'table-sort', params: currentSort });
        this.getPage();
    }

    onPageChange (page: number) {
        if (this.page === page) {
            return;
        }
        this.page = page;
        this.monEventsService.run('actionTrigger', { action: 'table-page-change', params: { page: page } });
        this.getPage();
    }

    onPageSizeChange (pageSize: string | number) {
        this.pageSize = Number(pageSize);
        this.page = 1;
        this.monEventsService.run('actionTrigger', { action: 'page-size-change', params: { pageSize: pageSize } });
        this.getPage();
    }

    onPageDetailsClose () {
        this.getPage();
    }
}
